import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { SignalRGroup } from "../../../components";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { FetchError, GetUserStrategicOwnershipDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

const useGetUserStrategicOwnershipRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getUserStrategicOwnershipRequest({
    queryKey,
  }: QueryFunctionContext<
    ReturnType<typeof queryKeys.users.getUserStrategicOwnership>
  >): Promise<GetUserStrategicOwnershipDto> {
    const accessToken = await getAccessToken();
    const [{ userId }] = queryKey;

    const response = await fetch(`${configuration.apiRootUrl}/users/${userId}/strategic-ownership`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getUserStrategicOwnershipRequest };
};

const useGetUserStrategicOwnership = (userId: string | undefined) => {
  const { getUserStrategicOwnershipRequest } = useGetUserStrategicOwnershipRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    if (userId !== undefined) {
      signalR.joinGroup(SignalRGroup.UserStrategicOwnershipItems(userId));
      return () => signalR.leaveGroup(SignalRGroup.UserStrategicOwnershipItems(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const handleShouldRefetchUserStrategicOwnershipItems = (userId: string) => {
      queryClient.invalidateQueries(queryKeys.users.getUserStrategicOwnership(userId));
    };

    signalR.onShouldRefetchUserStrategicOwnershipItems(handleShouldRefetchUserStrategicOwnershipItems);
    return () => signalR.offShouldRefetchUserStrategicOwnershipItems(handleShouldRefetchUserStrategicOwnershipItems);
  }, [queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.users.getUserStrategicOwnership(userId),
    queryFn: getUserStrategicOwnershipRequest,
    enabled: userId !== undefined,
    onSuccess: (data) => {
      data.actionItems.forEach((actionItem) => {
        queryClient.setQueryData(queryKeys.actionItems.getActionItemSummary(actionItem.guid), actionItem);
      });
    },
  });
};

export { useGetUserStrategicOwnership };
